/**
 * CheckInReviewAndCorrect - Lets the patient review and correct intake conversation results before submit.
 *
 * Shows summary, editable findings (label + value), and optional expandable transcript.
 * On confirm, calls onConfirm with the same shape as conversationResults but with updated findings/summary.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  TextField,
  Collapse,
  IconButton,
} from '@mui/material';
import { contentCardSx } from '../../styles/standaloneLayoutTokens';
import {
  CheckCircleOutlined,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
} from '@mui/icons-material';

/**
 * @param {Object} props
 * @param {Object} props.conversationResults - { title, findings: [{ label, value }], summary, transcript }
 * @param {function} props.onConfirm - Called with correctedConversationResults (same shape, updated findings/summary)
 */
const CheckInReviewAndCorrect = ({ conversationResults, onConfirm }) => {
  const [findings, setFindings] = useState([]);
  const [summary, setSummary] = useState('');
  const [transcriptOpen, setTranscriptOpen] = useState(false);

  useEffect(() => {
    if (!conversationResults) return;
    const raw = conversationResults.findings || [];
    setFindings(
      raw.map((f) =>
        typeof f === 'object' && f !== null && ('label' in f || 'value' in f)
          ? { label: f.label ?? '', value: f.value ?? '' }
          : { label: 'Item', value: String(f) }
      )
    );
    setSummary(conversationResults.summary ?? '');
  }, [conversationResults]);

  const handleFindingChange = (index, newValue) => {
    setFindings((prev) => {
      const next = [...prev];
      if (next[index]) next[index] = { ...next[index], value: newValue };
      return next;
    });
  };

  const handleSummaryChange = (e) => {
    setSummary(e.target.value);
  };

  const handleConfirm = () => {
    const corrected = {
      ...conversationResults,
      findings,
      summary,
    };
    onConfirm(corrected);
  };

  if (!conversationResults) return null;

  const transcript = conversationResults.transcript || [];
  const hasTranscript = Array.isArray(transcript) && transcript.length > 0;

  return (
    <Box sx={{ maxWidth: 600, mx: 'auto', p: 2 }}>
      <Paper elevation={0} sx={{ ...contentCardSx, p: 3 }}>
        <Typography variant="h6" fontWeight={600} gutterBottom>
          Review your answers
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Please review the information we collected. Edit any line that you want to correct, then confirm to continue.
        </Typography>

        {summary !== undefined && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" gutterBottom>
              Summary
            </Typography>
            <TextField
              fullWidth
              multiline
              minRows={2}
              value={summary}
              onChange={handleSummaryChange}
              variant="outlined"
              size="small"
            />
          </Box>
        )}

        {findings.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 1 }}>
              Information collected
            </Typography>
            {findings.map((f, idx) => (
              <Box key={idx} sx={{ mb: 1.5 }}>
                <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                  {f.label}
                </Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={f.value}
                  onChange={(e) => handleFindingChange(idx, e.target.value)}
                  variant="outlined"
                />
              </Box>
            ))}
          </Box>
        )}

        {hasTranscript && (
          <Box sx={{ mb: 2 }}>
            <Button
              size="small"
              startIcon={transcriptOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              onClick={() => setTranscriptOpen((o) => !o)}
              sx={{ textTransform: 'none' }}
            >
              {transcriptOpen ? 'Hide' : 'Show'} full conversation
            </Button>
            <Collapse in={transcriptOpen}>
              <Paper elevation={0} sx={{ ...contentCardSx, p: 2, mt: 1, bgcolor: 'grey.50', maxHeight: 300, overflow: 'auto' }}>
                {transcript.map((entry, i) => (
                  <Typography key={i} variant="body2" sx={{ mb: 0.5 }}>
                    <strong>{entry.speaker === 'assistant' ? 'Assistant' : 'You'}:</strong> {entry.text}
                  </Typography>
                ))}
              </Paper>
            </Collapse>
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleConfirm}
          startIcon={<CheckCircleOutlined />}
          size="large"
          fullWidth
          sx={{ mt: 2 }}
        >
          Confirm and continue
        </Button>
      </Paper>
    </Box>
  );
};

export default CheckInReviewAndCorrect;
